export const ANALYTICS_EVENT_TYPES = [
  'session_start',
  'session_heartbeat',
  'session_end',
  'page_enter',
  'page_exit',
  'page_hidden',
  'page_visible',
  'button_click',
  'scroll_milestone',
  'scroll_bottom',
  'date_change',
  'narrative_change',
  'token_filter_change',
  'narrative_view',
  'narrative_exit',
  'load_more',
  'event_detail_click',
  'detail_next_click',
  'detail_prev_click',
  'detail_timeline_click',
  'like_add',
  'like_remove',
  'event_explain_click',
  'policy_analysis_click',
  'policy_analysis_generated',
  'policy_analysis_failed',
  'policy_analysis_cache_hit',
  'policy_source_expand',
  'policy_region_change',
  'policy_chat_send',
  'policy_chat_success',
  'policy_chat_fail',
  'detail_explain_click',
  'event_view',
  'interpret_click',
  'polymarket_click',
  'follow_attempt',
  'follow_success',
  'unfollow_attempt',
  'unfollow_success',
  'my_follows_unfollow_click',
  'my_follows_timeline_click',
  'my_follows_explain_click',
  'my_follows_loaded',
  'timeline_chat_send',
  'timeline_chat_success',
  'timeline_chat_fail',
  'chat_send_click',
  'chat_message_received',
  'chat_send_success',
  'chat_send_fail',
  'markets_entry_click',
  'markets_page_loaded',
  'markets_narrative_filter',
  'markets_load_more',
  'market_detail_click',
  'market_detail_loaded',
  'market_detail_explain_click',
  'market_reason_expand',
  'charge_success',
  'insufficient_credits',
  'deposit_address_copy',
  'deposit_verify_click',
  'deposit_verify_success',
  'deposit_verify_fail',
  'slow_request',
  'api_error',
  'network_error',
] as const

export type AnalyticsEventType = typeof ANALYTICS_EVENT_TYPES[number]
export type AnalyticsEventLayer = 'session' | 'page' | 'ui' | 'business' | 'system'

export const ANALYTICS_PAGES = [
  'home',
  'event_detail',
  'explain',
  'policy_analysis',
  'timeline_chat',
  'follow_suggest',
  'my_follows',
  'balance',
  'spending',
  'markets',
  'market_detail',
  'unknown',
] as const

export type AnalyticsPage = typeof ANALYTICS_PAGES[number]

const SESSION_EVENTS = new Set<AnalyticsEventType>([
  'session_start', 'session_heartbeat', 'session_end',
])
const PAGE_EVENTS = new Set<AnalyticsEventType>([
  'page_enter', 'page_exit', 'page_hidden', 'page_visible',
  'scroll_milestone', 'scroll_bottom', 'event_view', 'narrative_view', 'narrative_exit',
])
const UI_EVENTS = new Set<AnalyticsEventType>(['button_click'])
const SYSTEM_EVENTS = new Set<AnalyticsEventType>([
  'charge_success', 'insufficient_credits', 'deposit_verify_success',
  'deposit_verify_fail', 'slow_request', 'api_error', 'network_error',
])

export function getEventLayer(eventType: AnalyticsEventType): AnalyticsEventLayer {
  if (SESSION_EVENTS.has(eventType)) return 'session'
  if (PAGE_EVENTS.has(eventType)) return 'page'
  if (UI_EVENTS.has(eventType)) return 'ui'
  if (SYSTEM_EVENTS.has(eventType)) return 'system'
  return 'business'
}

export interface AnalyticsRoute {
  page: AnalyticsPage
  route: string
  routeParams: Record<string, string>
}

const ROUTES: Array<{
  page: AnalyticsPage
  route: string
  pattern: RegExp
  params?: string[]
}> = [
  { page: 'home', route: '/', pattern: /^\/$/ },
  { page: 'my_follows', route: '/follows', pattern: /^\/follows\/?$/ },
  { page: 'balance', route: '/balance', pattern: /^\/balance\/?$/ },
  { page: 'spending', route: '/spending', pattern: /^\/spending\/?$/ },
  { page: 'markets', route: '/markets', pattern: /^\/markets\/?$/ },
  { page: 'market_detail', route: '/markets/:eventId', pattern: /^\/markets\/([^/]+)\/?$/, params: ['eventId'] },
  { page: 'explain', route: '/event/:id/explain', pattern: /^\/event\/([^/]+)\/explain\/?$/, params: ['id'] },
  { page: 'policy_analysis', route: '/event/:id/policy-analysis', pattern: /^\/event\/([^/]+)\/policy-analysis\/?$/, params: ['id'] },
  { page: 'timeline_chat', route: '/event/:id/timeline-chat', pattern: /^\/event\/([^/]+)\/timeline-chat\/?$/, params: ['id'] },
  { page: 'follow_suggest', route: '/event/:id/follow-suggest', pattern: /^\/event\/([^/]+)\/follow-suggest\/?$/, params: ['id'] },
  { page: 'event_detail', route: '/event/:id', pattern: /^\/event\/([^/]+)\/?$/, params: ['id'] },
]

export function resolveAnalyticsRoute(pathname: string): AnalyticsRoute {
  for (const candidate of ROUTES) {
    const match = pathname.match(candidate.pattern)
    if (!match) continue
    const routeParams: Record<string, string> = {}
    candidate.params?.forEach((name, index) => {
      routeParams[name] = decodeURIComponent(match[index + 1] || '')
    })
    return { page: candidate.page, route: candidate.route, routeParams }
  }
  return { page: 'unknown', route: 'unknown', routeParams: {} }
}
